/**
 * useTaskMetadata - Priority, difficulty, and custom field value changes
 */

export function useTaskMetadata(localSprint, setLocalSprint, recordAction) {
  const handlePriorityChange = (taskId, priority) => {
    const task = localSprint?.tasks?.find(t => t.id === taskId);
    const oldPriority = task?.priority;

    if (oldPriority === priority) {
      return;
    }

    setLocalSprint(prev => {
      if (!prev) {
        return prev;
      }
      const tasks = prev.tasks || [];
      const updatedTasks = tasks.map(t => 
        t.id === taskId ? { ...t, priority, updatedAt: new Date().toISOString() } : t
      );
      return {
        ...prev,
        tasks: updatedTasks,
      };
    });

    if (recordAction) {
      const taskName = task?.name || 'Task';
      recordAction({
        type: 'CHANGE_PRIORITY',
        description: `${taskName}: Priority changed`,
        undo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              tasks: prev.tasks.map(t => t.id === taskId ? { ...t, priority: oldPriority } : t),
            };
          });
        },
        redo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              tasks: prev.tasks.map(t => t.id === taskId ? { ...t, priority } : t),
            };
          });
        },
      });
    }
  };

  const handleDifficultyChange = (taskId, difficulty) => {
    const task = localSprint?.tasks?.find(t => t.id === taskId);
    const oldDifficulty = task?.difficulty;

    if (oldDifficulty === difficulty) {
      return;
    }

    setLocalSprint(prev => {
      if (!prev) {
        return prev;
      }
      const tasks = prev.tasks || [];
      const updatedTasks = tasks.map(t => 
        t.id === taskId ? { ...t, difficulty, updatedAt: new Date().toISOString() } : t
      );
      return {
        ...prev,
        tasks: updatedTasks,
      };
    });

    if (recordAction) {
      const taskName = task?.name || 'Task';
      recordAction({
        type: 'CHANGE_DIFFICULTY',
        description: `${taskName}: Difficulty changed`,
        undo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              tasks: prev.tasks.map(t => t.id === taskId ? { ...t, difficulty: oldDifficulty } : t),
            };
          });
        },
        redo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              tasks: prev.tasks.map(t => t.id === taskId ? { ...t, difficulty } : t),
            };
          });
        },
      });
    }
  };

  /**
   * Handle custom field value change on a task
   * @param {string} taskId - The task ID
   * @param {string} fieldId - The custom field ID
   * @param {any} value - The new value
   */
  const handleCustomFieldValueChange = (taskId, fieldId, value) => {
    const task = localSprint?.tasks?.find(t => t.id === taskId);
    const oldValues = task?.customFieldValues || {};
    const oldValue = oldValues[fieldId];

    // Skip if value hasn't changed
    if (oldValue === value) {
      return;
    }

    setLocalSprint(prev => {
      if (!prev) return prev;
      const tasks = prev.tasks || [];
      const updatedTasks = tasks.map(t => {
        if (t.id !== taskId) return t;
        const currentValues = t.customFieldValues || {};
        return {
          ...t,
          customFieldValues: {
            ...currentValues,
            [fieldId]: value,
          },
          updatedAt: new Date().toISOString(),
        };
      });
      return { ...prev, tasks: updatedTasks };
    });

    if (recordAction) {
      const taskName = task?.name || 'Task';
      const fieldDef = localSprint?.customFields?.find(f => f.id === fieldId);
      const fieldName = fieldDef?.name || 'Custom field';
      
      recordAction({
        type: 'CHANGE_CUSTOM_FIELD',
        description: `${taskName}: ${fieldName} changed`,
        undo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              tasks: prev.tasks.map(t => {
                if (t.id !== taskId) return t;
                const currentValues = t.customFieldValues || {};
                // If old value was undefined, remove the key entirely
                if (oldValue === undefined) {
                  const { [fieldId]: _, ...rest } = currentValues;
                  return { ...t, customFieldValues: rest };
                }
                return { ...t, customFieldValues: { ...currentValues, [fieldId]: oldValue } };
              }),
            };
          });
        },
        redo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              tasks: prev.tasks.map(t => {
                if (t.id !== taskId) return t;
                const currentValues = t.customFieldValues || {};
                return { ...t, customFieldValues: { ...currentValues, [fieldId]: value } };
              }),
            };
          });
        },
      });
    }
  };

  return {
    handlePriorityChange,
    handleDifficultyChange,
    handleCustomFieldValueChange,
  };
}

export default useTaskMetadata;
